import React, { useEffect, useState } from 'react';
import { FiKey, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import AppModal from '../../common/AppModal';
import services from '../../services';
import { toast } from 'react-toastify';

const AddClient = ({ show, onHide, setAuthUrl, setAddUserActive, authUrlData, setIsLoading }) => {
    const [formData, setFormData] = useState({
        client_id: '',
        project_id: '',
        auth_uri: '',
        token_uri: '',
        auth_provider_x509_cert_url: '',
        client_secret: '',
        redirect_uris: [''],
    });
    const [validated, setValidated] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleRedirectChange = (index, value) => {
        const updatedRedirectUris = [...formData.redirect_uris];
        updatedRedirectUris[index] = value;
        setFormData({ ...formData, redirect_uris: updatedRedirectUris });
    };

    const addRedirectUri = () => {
        setFormData({
            ...formData,
            redirect_uris: [...formData.redirect_uris, ''],
        });
    };

    const removeRedirectUri = (index) => {
        const updatedRedirectUris = formData.redirect_uris.filter((_, i) => i !== index);
        setFormData({ ...formData, redirect_uris: updatedRedirectUris });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setValidated(true);
        setErrorMessage('');
        setSuccessMessage('');
        setIsLoading(true);
        try {
            const response = await services.email.CreateEmailUser(formData);
            const client_id = response?.data?.data?.client_id;
            setSuccessMessage('Credential created successfully!');
            const urlResponse = await services.email.updateAuthEmail(client_id);
            setAuthUrl(urlResponse?.data?.authUrl);
            setTimeout(() => {
                onHide();
                setAddUserActive(true);
            }, 1000);
        } catch (error) {
            toast.error(error?.response?.data?.error);
            setErrorMessage(error.response?.data?.message || 'Failed to create credential');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (authUrlData && authUrlData.length > 0) {
            const firstItem = authUrlData[0];
            setFormData({
                client_id: firstItem.client_id || '',
                project_id: firstItem.project_id || '',
                auth_uri: firstItem.auth_uri || '',
                token_uri: firstItem.token_uri || '',
                auth_provider_x509_cert_url: firstItem.auth_provider_x509_cert_url || '',
                client_secret: firstItem.client_secret || '',
                redirect_uris: firstItem.redirect_uris || [''],
            });
        }
    }, [authUrlData]);

    const footerContent = (
        <button className="app-modal-btn-cancel" type="button" onClick={onHide}>
            Close
        </button>
    );

    return (
        <AppModal
            show={show}
            onClose={onHide}
            icon={<FiKey size={18} color="#fff" />}
            title="Create Credential"
            subtitle="Configure your Google OAuth credentials"
            maxWidth="620px"
            footer={footerContent}
        >
            {/* Alerts */}
            {errorMessage && (
                <div className="cred-alert-error">
                    <FiAlertCircle size={15} />
                    {errorMessage}
                </div>
            )}
            {successMessage && (
                <div className="cred-alert-success">
                    <FiCheckCircle size={15} />
                    {successMessage}
                </div>
            )}

            <form id="cred-form" noValidate onSubmit={handleSubmit}>

                {/* Row 1 — Client ID (full width) */}
                <div className="cred-form-group">
                    <label htmlFor="client_id" className="cred-form-label">Client ID</label>
                    <input
                        id="client_id"
                        type="text"
                        name="client_id"
                        className="cred-form-input"
                        value={formData.client_id}
                        onChange={handleInputChange}
                        placeholder="Enter Client ID"
                        required
                        disabled
                    />
                    {validated && !formData.client_id && (
                        <div className="cred-form-feedback">Please provide a valid Client ID.</div>
                    )}
                </div>

                {/* Row 2 — Project ID + Client Secret (2 cols) */}
                <div className="cred-form-grid">
                    <div className="cred-form-group">
                        <label htmlFor="project_id" className="cred-form-label">Project ID</label>
                        <input
                            id="project_id"
                            type="text"
                            name="project_id"
                            className="cred-form-input"
                            value={formData.project_id}
                            onChange={handleInputChange}
                            placeholder="Enter Project ID"
                            required
                            disabled
                        />
                        {validated && !formData.project_id && (
                            <div className="cred-form-feedback">Please provide a valid Project ID.</div>
                        )}
                    </div>
                    <div className="cred-form-group">
                        <label htmlFor="client_secret" className="cred-form-label">Client Secret</label>
                        <input
                            id="client_secret"
                            type="text"
                            name="client_secret"
                            className="cred-form-input"
                            value={formData.client_secret}
                            onChange={handleInputChange}
                            placeholder="Enter Client Secret"
                            required
                            disabled
                        />
                        {validated && !formData.client_secret && (
                            <div className="cred-form-feedback">Please provide a valid Client Secret.</div>
                        )}
                    </div>
                </div>

                {/* Row 3 — Auth URI + Token URI (2 cols) */}
                <div className="cred-form-grid">
                    <div className="cred-form-group">
                        <label htmlFor="auth_uri" className="cred-form-label">Auth URI</label>
                        <input
                            id="auth_uri"
                            type="url"
                            name="auth_uri"
                            className="cred-form-input"
                            value={formData.auth_uri}
                            onChange={handleInputChange}
                            placeholder="Enter Auth URI"
                            required
                            disabled
                        />
                        {validated && !formData.auth_uri && (
                            <div className="cred-form-feedback">Please provide a valid Auth URI.</div>
                        )}
                    </div>
                    <div className="cred-form-group">
                        <label htmlFor="token_uri" className="cred-form-label">Token URI</label>
                        <input
                            id="token_uri"
                            type="url"
                            name="token_uri"
                            className="cred-form-input"
                            value={formData.token_uri}
                            onChange={handleInputChange}
                            placeholder="Enter Token URI"
                            required
                            disabled
                        />
                        {validated && !formData.token_uri && (
                            <div className="cred-form-feedback">Please provide a valid Token URI.</div>
                        )}
                    </div>
                </div>

                {/* Row 4 — Cert URL (full width) */}
                <div className="cred-form-group">
                    <label htmlFor="auth_provider_x509_cert_url" className="cred-form-label">
                        Auth Provider X509 Cert URL
                    </label>
                    <input
                        id="auth_provider_x509_cert_url"
                        type="url"
                        name="auth_provider_x509_cert_url"
                        className="cred-form-input"
                        value={formData.auth_provider_x509_cert_url}
                        onChange={handleInputChange}
                        placeholder="Enter Auth Provider X509 Cert URL"
                        required
                        disabled
                    />
                    {validated && !formData.auth_provider_x509_cert_url && (
                        <div className="cred-form-feedback">Please provide a valid Cert URL.</div>
                    )}
                </div>

                {/* Row 5 — Redirect URIs (display only) */}
                <div className="cred-form-group">
                    <div className="cred-section-title">Redirect URIs</div>
                    {formData.redirect_uris.map((uri, index) => (
                        <div key={index} className="cred-redirect-row">
                            <input
                                type="url"
                                className="cred-form-input"
                                style={{ flex: 1 }}
                                value={uri}
                                onChange={(e) => handleRedirectChange(index, e.target.value)}
                                placeholder={`Redirect URI ${index + 1}`}
                                required
                                disabled
                            />
                        </div>
                    ))}
                </div>

            </form>
        </AppModal>
    );
};

export default AddClient;
